import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import SectionHeading from "@/components/base/SectionHeading";
import MovieShare from "@/pages/movie/components/MovieShare";
import {
  getCinemasForMovie,
  getMovieById,
  getRelatedMovies,
  getSchedulesByMovie,
  scheduleDates,
} from "@/lib/data";

export default function MovieDetailPage() {
  const { id = "" } = useParams();
  const movie = getMovieById(id);

  if (!movie) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center px-4 py-24 text-center md:px-8">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-error-warning-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">影片不存在</h1>
          <p className="mt-2 text-sm text-foreground-500">
            你访问的影片可能已下架或链接有误。
          </p>
          <Link
            to="/movies"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 hover:bg-primary-600"
          >
            返回电影分类
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const today = scheduleDates[0];
  const schedules = getSchedulesByMovie(movie.id).filter((s) => s.date === today);
  const movieCinemas = getCinemasForMovie(movie.id);
  const related = getRelatedMovies(movie, 4);

  const infoRows = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast.join("、") },
    { label: "类型", value: movie.genres.join(" / ") },
    { label: "地区", value: movie.region },
    { label: "片长", value: `${movie.duration} 分钟` },
    { label: "上映日期", value: movie.releaseDate },
    { label: "语言版本", value: movie.language },
    { label: "放映制式", value: movie.formats.join(" / ") },
  ];

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-8 md:py-10">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "电影分类", to: "/movies" },
            { label: movie.title },
          ]}
        />

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <div className="aspect-[2/3] w-full bg-background-200">
                <img
                  src={movie.poster}
                  alt={`${movie.title} 电影海报`}
                  title={`${movie.title} 九七电影院`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
            <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-4">
              <div className="flex items-center gap-3">
                <span className="font-heading text-3xl font-bold text-accent-600">
                  {movie.rating.toFixed(1)}
                </span>
                <div className="text-xs text-foreground-500">
                  <p>综合评分</p>
                  <p>热度指数 {movie.heat}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  movie.status === "now"
                    ? "bg-accent-100 text-accent-900"
                    : "bg-secondary-100 text-secondary-900"
                }`}
              >
                {movie.status === "now" ? "正在热映" : "即将上映"}
              </span>
              <span className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-600">
                {movie.year}
              </span>
            </div>
            <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {movie.title}
            </h1>

            <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
              {infoRows.map((row) => (
                <div key={row.label} className="flex gap-2 text-sm">
                  <dt className="shrink-0 text-foreground-500">{row.label}：</dt>
                  <dd className="text-foreground-800">{row.value}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-6">
              <h2 className="font-heading text-base font-semibold text-foreground-950">剧情简介</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{movie.synopsis}</p>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <a
                href="#screening"
                className="flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-calendar-check-line"></i>查看今日排片
              </a>
              <a
                href="https://movie.douban.com/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-chat-3-line"></i>用户评论
              </a>
            </div>

            <div className="mt-6 border-t border-background-200 pt-5">
              <MovieShare title={movie.title} />
            </div>
          </div>
        </div>

        {/* 排片影院 */}
        <section id="screening" className="mt-14">
          <SectionHeading
            title="今日排片影院"
            subtitle={`${today} 可购票影院与场次（点击场次查看购票指引）`}
            moreTo="/cinemas"
            moreLabel="更多影院"
          />
          {movieCinemas.length === 0 ? (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-12 text-center text-sm text-foreground-500">
              该影片暂无排片信息，请稍后再来看看。
            </div>
          ) : (
            <div className="space-y-4">
              {movieCinemas.map((cinema) => {
                const cinemaSchedules = schedules.filter((s) => s.cinemaId === cinema.id);
                return (
                  <div
                    key={cinema.id}
                    className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <Link
                          to={`/cinema/${cinema.id}`}
                          className="font-heading text-base font-semibold text-foreground-950 transition-colors hover:text-primary-600"
                        >
                          {cinema.name}
                        </Link>
                        <p className="mt-1 flex items-center gap-1 text-xs text-foreground-500">
                          <i className="ri-map-pin-line"></i>
                          {cinema.city} · {cinema.district} · {cinema.address}
                        </p>
                      </div>
                      <span className="text-xs font-semibold text-accent-600">{cinema.rating.toFixed(1)} 分</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {cinemaSchedules.length === 0 ? (
                        <span className="text-xs text-foreground-400">今日暂无场次</span>
                      ) : (
                        cinemaSchedules.map((s) => (
                          <Link
                            key={s.id}
                            to={`/schedule/${s.id}`}
                            className="flex flex-col rounded-md border border-background-300 px-3 py-1.5 text-xs transition-colors hover:border-accent-400 hover:bg-accent-50"
                          >
                            <span className="font-semibold text-foreground-900">{s.time}</span>
                            <span className="text-foreground-500">
                              {s.hall} · ¥{s.price}
                            </span>
                          </Link>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* 相关推荐 */}
        <section className="mt-14">
          <SectionHeading title="相关推荐" subtitle="同类型与热门影片推荐" />
          <div
            data-product-shop
            className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4"
          >
            {related.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}